import { Box, Stack, Tooltip } from '@mui/material'
import { CheckCircleIcon, PaperPlaneTiltIcon, SealCheckIcon } from '@phosphor-icons/react'
import { assembleNameWithEmailFallback, ContentChip, LoadingSkeleton, tokens, User, UserBubble } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { useTranslation } from 'react-i18next'
import { isPlausibilityVisibleToAppraisers } from '@einwert/valuation-api-client'

type HandoverStatusActionProps = {
    isValuationPlausibilityPending?: boolean
    isHandedOver?: boolean
    isApproved?: boolean
    isInternallyApproved?: boolean
    handedOverByUser?: User
    approvedByUser?: User
    internallyApprovedByUser?: User
    formattedHandoverDate?: string
    formattedApprovalDate?: string
    formattedInternalApprovalDate?: string
    handoverTooltip?: string
    isHandoverDisabled: boolean
    onOpenHandoverModal: () => void
    chipSize?: 'small' | 'medium'
    handoverInfoText?: string
    sharedChipLabel?: string
    sharedByText?: (userName: string) => string
    sharedOnText?: (date: string) => string
    sharedFallbackLabel?: string
    shareButtonLabel?: string
    shareDescription?: string
    unknownUserLabel?: string
}

type ApprovalStatusActionProps = {
    isValuationPlausibilityPending?: boolean
    isApproved?: boolean
    approvedByUser?: User
    formattedApprovalDate?: string
    approvalTooltip?: string
    isApprovalDisabled?: boolean
    onOpenApprovalModal: () => void
    align?: 'left' | 'right'
    approvedChipLabel?: string
    approvedTooltipLabel?: string
    openApprovalDescription?: string
    approvedInfoText?: string
    chipSize?: 'small' | 'medium'
    sectionLabel?: string
    approvedOnText?: (date: string) => string
    approvedFallbackLabel?: string
    approveButtonLabel?: string
    unknownUserLabel?: string
}

export function HandoverStatusAction({
    isValuationPlausibilityPending,
    isHandedOver,
    isApproved,
    isInternallyApproved,
    handedOverByUser,
    approvedByUser,
    internallyApprovedByUser,
    formattedHandoverDate,
    formattedApprovalDate,
    formattedInternalApprovalDate,
    handoverTooltip,
    isHandoverDisabled,
    onOpenHandoverModal,
    chipSize = 'medium',
    handoverInfoText,
    sharedChipLabel,
    sharedByText,
    sharedOnText,
    sharedFallbackLabel,
    shareButtonLabel,
    shareDescription,
    unknownUserLabel,
}: HandoverStatusActionProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tStatusActions } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.handoverApprovalStatusActions',
    })
    const isVisibleToAppraisers = isPlausibilityVisibleToAppraisers({
        is_handed_over: isHandedOver,
        is_approved: isApproved,
        is_internally_approved: isInternallyApproved,
    })
    const handoverTooltipUser = handedOverByUser ?? approvedByUser ?? internallyApprovedByUser
    const handoverUserName = assembleNameWithEmailFallback(handoverTooltipUser ?? {}) || (unknownUserLabel ?? tCommon('unknown'))
    const handoverDate = formattedHandoverDate ?? formattedApprovalDate ?? formattedInternalApprovalDate
    const getSharedByText = sharedByText ?? ((userName: string) => tStatusActions('handover.sharedByTooltip', { user: userName }))
    const getSharedOnText = sharedOnText ?? ((date: string) => tStatusActions('handover.sharedOn', { date }))

    return (
        <Stack direction="column" gap={tokens.SizingSpacing100}>
            {isValuationPlausibilityPending ? (
                <LoadingSkeleton variant="rounded" width="100%" height="64px" />
            ) : isVisibleToAppraisers ? (
                <>
                    <ContentChip
                        size={chipSize}
                        color="green"
                        icon={<CheckCircleIcon size={tokens.SizingIconS} weight="fill" />}
                        label={sharedChipLabel ?? tStatusActions('handover.chipLabel')}
                    />
                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                        {handoverTooltipUser && (
                            <Tooltip title={getSharedByText(handoverUserName)} placement="top">
                                <Box sx={{ '& .MuiAvatar-root': { scale: 1, border: 'unset' } }}>
                                    <UserBubble
                                        profileImageUrl={handoverTooltipUser.profile_thumbnail_image_url}
                                        profileFallbackImageUrl={handoverTooltipUser.profile_image_url}
                                        size={tokens.SizingIconS}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {handoverDate ? getSharedOnText(handoverDate) : (sharedFallbackLabel ?? tStatusActions('handover.shared'))}
                        </Typography>
                    </Stack>
                    {handoverInfoText && isHandedOver && !isApproved && (
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {handoverInfoText}
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <Tooltip title={handoverTooltip} placement="top">
                        <span style={{ display: 'inline-flex', width: 'fit-content' }}>
                            <TextButtonNew
                                variant="secondary"
                                leftIcon={<PaperPlaneTiltIcon />}
                                label={shareButtonLabel ?? tPlausibility('shareAnnotations')}
                                onClick={onOpenHandoverModal}
                                disabled={isHandoverDisabled}
                            />
                        </span>
                    </Tooltip>
                    <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                        {shareDescription ?? tStatusActions('handover.actionDescription')}
                    </Typography>
                </>
            )}
        </Stack>
    )
}

export function ApprovalStatusAction({
    isValuationPlausibilityPending,
    isApproved,
    approvedByUser,
    formattedApprovalDate,
    approvalTooltip,
    isApprovalDisabled = false,
    onOpenApprovalModal,
    align = 'left',
    approvedChipLabel,
    approvedTooltipLabel,
    openApprovalDescription,
    approvedInfoText,
    chipSize = 'medium',
    sectionLabel,
    approvedOnText,
    approvedFallbackLabel,
    approveButtonLabel,
    unknownUserLabel,
}: ApprovalStatusActionProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tStatusActions } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.handoverApprovalStatusActions',
    })
    const approvedByName = assembleNameWithEmailFallback(approvedByUser ?? {}) || (unknownUserLabel ?? tCommon('unknown'))
    const getApprovedOnText = approvedOnText ?? ((date: string) => tStatusActions('approval.approvedOn', { date }))
    const approvalDescription = openApprovalDescription ?? tStatusActions('approval.openApprovalDescription')

    return (
        <Stack direction="column" gap={tokens.SizingSpacing100} alignItems={align === 'right' ? 'flex-end' : 'flex-start'}>
            {sectionLabel && (
                <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                    {sectionLabel}
                </Typography>
            )}
            {isValuationPlausibilityPending ? (
                <LoadingSkeleton variant="rounded" width="100%" height="64px" />
            ) : isApproved ? (
                <>
                    <ContentChip
                        size={chipSize}
                        color="green"
                        icon={<SealCheckIcon size={tokens.SizingIconS} weight="fill" />}
                        label={approvedChipLabel ?? tPlausibility('draftApproved')}
                    />
                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                        {approvedByUser && (
                            <Tooltip title={`${approvedTooltipLabel ?? tPlausibility('approvedBy')} ${approvedByName}`} placement="top">
                                <Box sx={{ '& .MuiAvatar-root': { scale: 1, border: 'unset' } }}>
                                    <UserBubble
                                        profileImageUrl={approvedByUser.profile_thumbnail_image_url}
                                        profileFallbackImageUrl={approvedByUser.profile_image_url}
                                        size={tokens.SizingIconS}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                            {formattedApprovalDate
                                ? getApprovedOnText(formattedApprovalDate)
                                : (approvedFallbackLabel ?? tStatusActions('approval.approved'))}
                        </Typography>
                    </Stack>
                    {approvedInfoText && (
                        <Box textAlign={align === 'right' ? 'right' : 'left'}>
                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                {approvedInfoText}
                            </Typography>
                        </Box>
                    )}
                </>
            ) : (
                <>
                    <Tooltip title={approvalTooltip} placement="top">
                        <span style={{ display: 'inline-flex', width: 'fit-content' }}>
                            <TextButtonNew
                                variant="secondary"
                                leftIcon={<SealCheckIcon />}
                                label={approveButtonLabel ?? tCommonActions('approve')}
                                onClick={onOpenApprovalModal}
                                disabled={isApprovalDisabled}
                            />
                        </span>
                    </Tooltip>
                    {approvalDescription && (
                        <Box textAlign={align === 'right' ? 'right' : 'left'}>
                            <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                {approvalDescription}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Stack>
    )
}
